import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Icon,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination,
  CircularProgress,
  Tooltip,
  Box,
  Chip,
  Divider,
  Autocomplete,
  Paper,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
} from "@mui/material";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../common/CommonActions";
import { ErrorMessageResolver } from "../../common/ErrorMessageResolver";
import { CarrierRepository } from "../../repositories/CarrierRepository";
import CreateCarrierFromConsignmentDialog from "./CreateCarrierFromConsignmentDialog";
import { OrganizationsRepository } from "../../repositories/OrganizationsRepository";
import { LOCALE } from "../../properties/Locale";
import { AuthRepository } from "../../repositories/AuthRepository";

/**
 * Backend expectation:
 * CarrierRepository.search(paramsObject) where paramsObject is a JSON object like:
 * {
 *   name, eori, clientId,
 *   page, size, sort
 * }
 * Returns: { data: { content: [...], totalElements: number } }
 */
function ManageCarriersDialog({
  open,
  onClose,
  onUse,
  organization = AuthRepository.getUserDetails().organization, // optional default client (preselected)
  pageSizeOptions = [5, 10, 25],
  allowEdit = false,
}) {
  const dispatch = useDispatch();

  // Filters (only EORI, Name, Client)
  const [filters, setFilters] = useState({
    name: "",
    eori: "",
    client: null, // object from clients list
  });

  // Table state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(pageSizeOptions?.[1] || 10);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientInput, setClientInput] = useState("");
  // Modals
  const [creating, setCreating] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState(null);

  const nameRef = useRef(null);
  const getClientLabel = (o) => (o && `${o.nameEn || o.name || ""}`) || "";

  const clientOptions = useMemo(() => {
    // Merge provided clients with organization (if any) without dupes by id
    const list = [...(clients || [])];
    if (organization && !list.some((c) => c?.id === organization?.id)) {
      list.unshift(organization);
    }
    return list;
  }, [clients]);

  // Build clean JSON (key→value) with only non-empty values
  const buildParams = (pageArg = page, sizeArg = size) => {
    const params = {
      name: filters.name?.trim() || undefined,
      eori: filters.eori?.trim() || undefined,
      clientId: filters.client?.id || undefined,
      type: filters.type || undefined,
      page: pageArg,
      size: sizeArg,
      sort: "name,asc",
    };
    Object.keys(params).forEach(
      (k) => params[k] === undefined && delete params[k]
    );
    return params;
  };

  // Organizations lookup
  useEffect(() => {
    if (clientInput?.length >= 3) {
      OrganizationsRepository.getBySearchString(clientInput)
        .then((res) => {
          setClients(res.data.filter((org) => org.type === "CLIENT"));
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [clientInput]);

  const fetchData = async (pageArg = page, sizeArg = size) => {
    try {
      setLoading(true);
      const res = await CarrierRepository.search(buildParams(pageArg, sizeArg));
      const content = res?.data?.content ?? res?.data ?? [];
      const totalElements = res?.data?.totalElements ?? content.length;
      setRows(content);
      setTotal(totalElements);

      // Keep selection valid
      if (content.length === 0) setSelected(null);
      else if (selected && !content.find((r) => r.id === selected.id))
        setSelected(null);
    } catch (err) {
      console.error(err);
      dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
    } finally {
      setLoading(false);
    }
  };

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setFilters({
      name: "",
      eori: "",
      client: organization || null,
    });
    setSelected(null);
    setPage(0);
    setTimeout(() => nameRef.current?.focus(), 50);
    fetchData(0, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pagination handlers
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchData(newPage, size);
  };
  const handleChangeRowsPerPage = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setSize(newSize);
    setPage(0);
    fetchData(0, newSize);
  };

  // Search / Reset
  const handleSearch = () => {
    setPage(0);
    fetchData(0, size);
  };

  const handleReset = () => {
    setFilters({
      name: "",
      eori: "",
      client: organization || null,
    });
    setPage(0);
    fetchData(0, size);
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Delete item "${row.name}"? This cannot be undone.`))
      return;
    try {
      setLoading(true);
      await CarrierRepository.deactivate(row.id);
      dispatch(notifyShowSuccessMessage("Successfully deleted."));
      const newTotal = Math.max(total - 1, 0);
      const newLastPage = Math.max(Math.ceil(newTotal / size) - 1, 0);
      const targetPage = Math.min(page, newLastPage);
      setPage(targetPage);
      fetchData(targetPage, size);
      if (selected?.id === row.id) setSelected(null);
    } catch (err) {
      console.error(err);
      dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
    } finally {
      setLoading(false);
    }
  };

  const countryCity = (row) => {
    const c = row?.country?.name || row?.country?.isoCode || "";
    const city = row?.city?.cityName || row?.city?.postCode || "";
    if (c && city) return `${c} • ${city}`;
    return c || city || "";
  };

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon>local_shipping</Icon>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <span style={{ fontSize: 18, fontWeight: 600 }}>
                  Manage Carriers / Declarants
                </span>
                <Chip
                  label={`${total} total`}
                  size="small"
                  variant="outlined"
                  icon={<Icon fontSize="small">format_list_bulleted</Icon>}
                />
              </Box>
              <Box sx={{ color: "text.secondary", fontSize: 13 }}>
                Search by EORI and Name.
              </Box>
            </Box>
            <Box flexGrow={1} />
            <Button
              onClick={() => setCreating(true)}
              variant="contained"
              startIcon={<Icon>add</Icon>}
              size="small"
            >
              New Carrier / Declarant
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {/* Filters */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={3}>
                <TextField
                  inputRef={nameRef}
                  label="Name"
                  fullWidth
                  size="small"
                  value={filters.name}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      name: e.target.value.replace(/\s{2,}/g, " "),
                    }))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>badge</Icon>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ autoComplete: "new-password" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="EORI"
                  fullWidth
                  size="small"
                  value={filters.eori}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      eori: e.target.value.toUpperCase(),
                    }))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>verified_user</Icon>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ autoComplete: "new-password", maxLength: 25 }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={clientOptions}
                  value={filters.client}
                  onChange={(_, v) =>
                    setFilters((p) => ({ ...p, client: v || null }))
                  }
                  inputValue={clientInput}
                  onInputChange={(_, nv) => setClientInput(nv || "")}
                  getOptionLabel={getClientLabel}
                  isOptionEqualToValue={(o, v) =>
                    String(o?.id) === String(v?.id)
                  }
                  noOptionsText={LOCALE?.noOptionsText ?? "No options"}
                  size="small"
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={LOCALE?.organization ?? "Client"}
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Icon>business</Icon>
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: "new-password",
                      }}
                    />
                  )}
                  clearOnEscape
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    style={{ textAlign: "left" }}
                    value={filters.type}
                    onChange={(e) => {
                      setFilters((p) => ({
                        ...p,
                        type: e.target.value.toUpperCase(),
                      }));
                    }}
                    fullWidth
                    label="Type"
                  >
                    <MenuItem value="">
                      <span style={{ marginLeft: 10 }}>All</span>
                    </MenuItem>
                    <MenuItem value={"CARRIER"}>
                      <span style={{ marginLeft: 10 }}>Carrier</span>
                    </MenuItem>
                    <MenuItem value={"DECLARANT"}>
                      <span style={{ marginLeft: 10 }}>Declarant</span>
                    </MenuItem>
                    <MenuItem value={"CARRIER_AND_DECLARANT"}>
                      <span style={{ marginLeft: 10 }}>
                        Carrier & Declarant
                      </span>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1}>
                <Button
                  onClick={handleSearch}
                  variant="contained"
                  size="small"
                  startIcon={<Icon>search</Icon>}
                >
                  Search
                </Button>
                <Button
                  onClick={handleReset}
                  variant="text"
                  size="small"
                  startIcon={<Icon>restart_alt</Icon>}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Results table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>EORI</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Country / City</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={3}
                        gap={1}
                      >
                        <CircularProgress size={20} />
                        <span>Loading carriers…</span>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box textAlign="center" py={3} color="text.secondary">
                        No results found. Adjust filters and search again.
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {!loading &&
                  rows.map((row) => {
                    const isSelected = selected?.id === row.id;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        selected={isSelected}
                        onClick={() => setSelected(row)}
                        onDoubleClick={() => onUse(row)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small">badge</Icon>
                            <Tooltip title={row?.name || ""} placement="top">
                              <span>{row?.name || "-"}</span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>{row?.eori || "-"}</TableCell>
                        <TableCell>
                          {row?.organization?.nameEn ||
                            row?.organization?.name ||
                            "-"}
                        </TableCell>
                        <TableCell>{countryCity(row) || "-"}</TableCell>
                        <TableCell>{row?.contactEmail || "-"}</TableCell>
                        <TableCell>{row?.contactPhone || "-"}</TableCell>
                        <TableCell>
                          {row?.type === "CARRIER_AND_DECLARANT" ? (
                            <Tooltip title="CARRIER AND DECLARANT">
                              <Chip
                                label="C,D"
                                size="small"
                                style={{ fontWeight: "bold" }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title={row?.type || "-"}>
                              <Chip
                                label={row?.type?.[0] || "-"}
                                size="small"
                                style={{ fontWeight: "bold" }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box display="inline-flex" gap={0.5}>
                            {allowEdit && (
                              <Tooltip title="Edit">
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={(e) => {
                                    setEditingCarrier(row);
                                  }}
                                  startIcon={<Icon>edit</Icon>}
                                >
                                  Edit
                                </Button>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <Button
                                size="small"
                                color="error"
                                variant="text"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(row);
                                }}
                                startIcon={<Icon>delete</Icon>}
                              >
                                Delete
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={size}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={pageSizeOptions}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            startIcon={<Icon>close</Icon>}
            variant="text"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create */}
      {creating && (
        <CreateCarrierFromConsignmentDialog
          open={creating}
          onClose={() => setCreating(false)}
          onSave={(created) => {
            setCreating(false);
            dispatch(notifyShowSuccessMessage("Carrier created."));
            setPage(0);
            fetchData(0, size);
            setSelected(created);
          }}
          organization={organization || filters.client || undefined}
        />
      )}

      {/* Edit (optional) */}
      {allowEdit && editingCarrier && (
        <CreateCarrierFromConsignmentDialog
          open={!!editingCarrier}
          onClose={() => setEditingCarrier(null)}
          onSave={(updated) => {
            setEditingCarrier(null);
            dispatch(notifyShowSuccessMessage("Carrier updated."));
            fetchData(page, size);
            setSelected(updated);
          }}
          organization={organization || filters.client || undefined}
          initialValue={editingCarrier}
        />
      )}
    </>
  );
}

ManageCarriersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** When user chooses a carrier (Use / double-click). Receives the carrier object. */
  onUse: PropTypes.func.isRequired,
  /** Default client; also added to client options and preselected. */
  organization: PropTypes.object,
  /** Optional list of clients for the Client Autocomplete (objects with at least { id, name/nameEn }). */
  clients: PropTypes.array,
  pageSizeOptions: PropTypes.array,
  /** If true, shows Edit action that opens the Create dialog prefilled. */
  allowEdit: PropTypes.bool,
};

export default ManageCarriersDialog;
